import React, { createContext, useContext, ReactNode } from 'react';
import { toast, ToastOptions } from 'react-toastify';
import { AppToast, ToastVariant } from '../components/AppToast';

interface ToastContextType {
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
  showDelete: (title: string, message?: string) => void;
  showFavorite: (title: string, message?: string) => void;
  showSettings: (title: string, message?: string) => void;
  showBook: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const optionsFor = (variant: ToastVariant): ToastOptions => ({
  position: 'top-right',
  autoClose: 4500,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: false,
  icon: false,
  type: variant,
  className: `bs-toast bs-toast--${variant}`,
});

const notify = (variant: ToastVariant, title: string, message?: string) => {
  toast(<AppToast title={title} message={message} variant={variant} />, optionsFor(variant));
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const value: ToastContextType = {
    showSuccess: (title, message) => notify('success', title, message),
    showError: (title, message) => notify('error', title, message),
    showWarning: (title, message) => notify('warning', title, message),
    showInfo: (title, message) => notify('info', title, message),
    showDelete: (title, message) => notify('error', title, message),
    showFavorite: (title, message) => notify('success', title, message),
    showSettings: (title, message) => notify('info', title, message),
    showBook: (title, message) => notify('success', title, message),
  };

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export default ToastProvider;
